import React, { useState } from 'react';
import { caseStudies } from '../data/case-studies';
import useAnimationOnScroll from '../hooks/useAnimationOnScroll';
import emailService from '../services/emailService';
import { CheckCircle, AlertCircle } from 'lucide-react';
import './CaseStudiesOverview.css';

const CaseStudiesOverview = () => {
    const headerRef = useAnimationOnScroll();
    const gridRef = useAnimationOnScroll();
    const [selectedCaseStudy, setSelectedCaseStudy] = useState(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        phone: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    const handleCaseStudyClick = (e, study) => {
        e.preventDefault();
        setSelectedCaseStudy(study);
        setIsPopupOpen(true);
    };

    const closePopup = () => {
        setIsPopupOpen(false);
        setSelectedCaseStudy(null);
        setFormData({ name: '', email: '', company: '', phone: '' });
        setSubmitStatus(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        // Basic validation
        if (!formData.name.trim() || !formData.email.trim()) {
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const emailResult = await emailService.sendCaseStudyEmail(
                formData,
                selectedCaseStudy
            );

            if (emailResult.success) {
                setSubmitStatus('success');
                // Reset form data
                setFormData({ name: '', email: '', company: '', phone: '' });
                // Close popup after a short delay to show success message
                setTimeout(() => {
                    closePopup();
                }, 2000);
            } else {
                throw new Error(emailResult.message || 'Failed to send email');
            }
        } catch (error) {
            console.error('Case study form submission error:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="section case-studies-overview-section">
            <div className="container">
                <div ref={headerRef} className="section-header animate-in">
                    <h2 className="section-title">Case Studies</h2>
                    <p className="section-subtitle">
                        Real-world examples of how we've helped organizations achieve greater outcomes.
                    </p>
                </div>

                <div ref={gridRef} className="case-studies-grid animate-in">
                    {caseStudies.map((study, index) => (
                        <div
                            key={study.id}
                            className="case-study-card"
                            onClick={(e) => handleCaseStudyClick(e, study)}
                        >
                            <h3 className="case-study-title">{study.title}</h3>
                            <p className="case-study-description">{study.description}</p>

                            {/* Enhanced teaser content */}
                            <div className="case-study-teaser">
                                <div className="teaser-challenge">
                                    <strong>Challenge:</strong> {study.challenge.substring(0, 120)}...
                                </div>
                                <div className="teaser-results">
                                    <strong>Results:</strong> {study.results[0]}
                                </div>
                            </div>

                            {/* Tags section */}
                            <div className="case-study-tags">
                                {study.tags.slice(0, 3).map((tag, tagIndex) => (
                                    <span key={tagIndex} className="case-study-tag">
                                        {tag}
                                    </span>
                                ))}
                            </div>

                            <div className="case-study-cta">
                                <span>Request Case Study</span>
                                <div className="cta-arrow">→</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Case Study Download Popup */}
            {isPopupOpen && selectedCaseStudy && (
                <div className="case-study-popup-overlay" onClick={closePopup}>
                    <div className="case-study-popup" onClick={(e) => e.stopPropagation()}>
                        <button className="popup-close-btn" onClick={closePopup}>×</button>

                        <div className="popup-header">
                            <h3 className="popup-title">Request Case Study</h3>
                            <p className="popup-subtitle">
                                Get complete details about "{selectedCaseStudy.title}"
                            </p>
                        </div>

                        <div className="popup-content">
                            <div className="case-study-preview">
                                <h4>Case Study Preview</h4>
                                <p><strong>Challenge:</strong> {selectedCaseStudy.challenge}</p>
                                <p><strong>Solution:</strong> {selectedCaseStudy.solution}</p>
                                <p><strong>Results:</strong> {selectedCaseStudy.results.join(', ')}</p>
                            </div>

                            <form className="download-form" onSubmit={handleFormSubmit}>
                                <div className="form-group">
                                    <label htmlFor="name">Full Name *</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Enter your full name"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="email">Email Address *</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="We'll email the case study to this address"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="company">Company</label>
                                    <input
                                        type="text"
                                        id="company"
                                        name="company"
                                        value={formData.company}
                                        onChange={handleInputChange}
                                        placeholder="Enter your company name"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="phone">Phone Number</label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        placeholder="Enter your phone number"
                                    />
                                </div>

                                {submitStatus === 'success' && (
                                    <div className="form-success-message">
                                        <CheckCircle size={20} />
                                        <span>Thank you! We'll review your request and get back to you shortly.</span>
                                    </div>
                                )}

                                {submitStatus === 'error' && (
                                    <div className="form-error-message">
                                        <AlertCircle size={20} />
                                        <span>Failed to submit. Please try again.</span>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    className="download-btn"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Sending...' : 'Request Case Study'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default CaseStudiesOverview;
